import styled from '@emotion/styled'
import { SerializedStyles, css } from '@emotion/react'
import { useState, useEffect } from 'react'
import {
  zoomEffect,
  moveEffect,
  ZoomDirection,
  MoveDirection,
  EffectStrength,
  transitionEffect,
} from './FullscreenImageEffects'
import { ScrollOverMediaDocumentDataBodyImageStepSlicePrimary } from '../../../../../types.generated'
import { useIsPortrait } from '../../../../util/breakpoints'
import { LazyImage } from '../../../atoms/LazyImage'
import isPropValid from '@emotion/is-prop-valid'
import { scrollstoryBackgroundColor } from '../../../atoms/Atoms'
import { VIEWPORT_HEIGHT } from '../../../../util/viewportHeight'

type EffectType =
  ScrollOverMediaDocumentDataBodyImageStepSlicePrimary['effect_type']
export type TransitionType =
  ScrollOverMediaDocumentDataBodyImageStepSlicePrimary['transition_type']
export type EffectPlayback =
  ScrollOverMediaDocumentDataBodyImageStepSlicePrimary['effect_playback']
type ImageType = ScrollOverMediaDocumentDataBodyImageStepSlicePrimary['image']

const getImageUrl = (link: ImageType, usePortrait: boolean): string => {
  if (usePortrait && link.portrait?.url) {
    return link.portrait.url ?? ''
  }

  return link.landscape?.url ?? link.url ?? ''
}

const StyledImage = styled(LazyImage, {
  shouldForwardProp: (prop) => isPropValid(prop),
})<{
  effect?: SerializedStyles
  transition?: SerializedStyles
  useBackgroundColor: boolean
}>`
  object-fit: cover;
  height: ${VIEWPORT_HEIGHT};
  width: 100%;
  ${(props) => (props.effect ? props.effect : '')}
  ${(props) => (props.transition ? props.transition : '')}

  ${({ useBackgroundColor }) =>
    useBackgroundColor ? scrollstoryBackgroundColor : ''}
`

const effectFactory = (props: FullscreenImageProps): SerializedStyles => {
  if (!props.effectType) {
    return css``
  }

  const effectBase = props.effectType.split('_')[0]
  if (effectBase === 'none') {
    return css``
  }

  if (effectBase === 'zoomIn' || effectBase === 'zoomOut') {
    const direction = props.effectType.split('_')[1] as ZoomDirection
    return zoomEffect(
      effectBase,
      direction,
      props.effectPlayback ?? 'play_once',
      props.effectStrength ?? 'default',
      props.scrollPercentage
    )
  } else if (effectBase === 'move') {
    const direction = props.effectType.split('_')[1] as MoveDirection
    return moveEffect(
      direction,
      props.effectPlayback ?? 'play_once',
      props.effectStrength ?? 'default',
      props.scrollPercentage
    )
  } else {
    console.error('Unsupported image effect type: ' + props.effectType)
  }

  return css``
}

const transitionFactory = (
  transitionType: TransitionType,
  scrollPercentage: number
): SerializedStyles => {
  if (!transitionType) {
    return css``
  }

  if (transitionType === 'fade (default)') {
    return css``
  } else if (
    transitionType === 'wipe_right-to-left' ||
    transitionType === 'wipe_left-to-right'
  ) {
    return transitionEffect(transitionType, scrollPercentage)
  } else {
    console.error('Unsupported image transition type: ' + transitionType)
    return css``
  }
}

interface FullscreenImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  image: ImageType
  isActive: boolean
  scrollPercentage: number
  amountOfStepItems: number
  effectType: EffectType
  effectPlayback: EffectPlayback
  effectStrength: EffectStrength
  transitionType: TransitionType
  useBackgroundColor: boolean
}

export const FullscreenImage = (props: FullscreenImageProps) => {
  const [activeEffect, setActiveEffect] = useState<
    SerializedStyles | undefined
  >(undefined)
  const [activeTransition, setActiveTransition] = useState<
    SerializedStyles | undefined
  >(undefined)

  const isPortrait = useIsPortrait()

  const imageUrl = getImageUrl(props.image, isPortrait)

  useEffect(() => {
    let timeoutId: number | null = null

    if (props.isActive && props.effectType) {
      setActiveEffect(effectFactory(props))
    }

    if (!props.isActive && props.effectPlayback === 'play_once') {
      // play_once effects should be removed 50ms after image is not visible anymore (to avoid flickering)
      timeoutId = setTimeout(() => {
        setActiveEffect(undefined)
        // background transition to fade away already takes 500ms
      }, 550)
    }

    if (props.transitionType) {
      const startPercentageOfLastBox = 1 - 1 / props.amountOfStepItems
      const isOnLastStepTextBox =
        props.scrollPercentage > startPercentageOfLastBox
      if (props.isActive && isOnLastStepTextBox) {
        const percentageOnLastBox =
          (props.scrollPercentage - startPercentageOfLastBox) /
          (1 / props.amountOfStepItems)
        setActiveTransition(
          transitionFactory(props.transitionType, percentageOnLastBox)
        )
      } else {
        setActiveTransition(undefined)
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [props])

  const dynamicDataCy = `fullscreen-image-${props.effectType || 'none'}-${props.isActive ? 'active' : 'inactive'}`
  return (
    <StyledImage
      src={imageUrl}
      {...props}
      effect={activeEffect}
      transition={activeTransition}
      data-cy={dynamicDataCy}
    />
  )
}
